import React, { useState, useEffect, useCallback, FormEvent } from 'react'
import { useRouter } from 'next/router'
import { DecorativeBlocks, Header } from '@educator/components'
import { ThemeColor } from '@educator/components/ThemeColor'
import { initAndSignInWithApple } from '@educator/utils/appleAuth'
import type { CoreSessionAccount } from '@educator/utils/coreSession'
import { signOutOfCore } from '@educator/utils/logout'
import { paths } from '@educator/utils/paths'
import { getSafeReturnTo } from '@educator/utils/returnTo'
import { emailSchema } from '@educator/utils/validation'
import { isGDPRCountry } from '@mc/gdpr'
import { useGeo } from '@mc/geo-react'
import { useTranslation } from '@mc/i18n'
import { trackError } from '@mc/monitoring'
import {
  useIsSocialLoginsFacebookEnabled,
  useIsSocialLoginsGoogleEnabled,
} from 'experiments'
import { useCohortDetails } from '../../api-hooks/enrollment/useCohortDetails'
import { getBasePath } from '../../config/basePath'
import {
  TrackClick,
  TrackPageViewed,
  TrackOnMount,
  TrackingAttributes,
  useFireEvent,
  educatorPageNames,
  GOLD_STANDARD_INTEGRATIONS,
} from '../../tracking/educatorTracking'
import { getFbCookies } from '../../utils/getFbCookies'
import {
  CoreSessionContinue,
  SocialLoginButtons,
  TermsText,
} from './components'

type SocialProvider = 'google' | 'apple' | 'facebook'

const ERROR_MESSAGES: Record<string, string> = {
  apple_signup_blocked: 'emailEntry.appleSignupBlocked',
  auth_failed: 'emailEntry.authFailed',
  token_exchange_failed: 'emailEntry.authFailed',
  invalid_state: 'emailEntry.authFailed',
}

interface EmailEntryPageProps {
  coreAccount?: CoreSessionAccount | null
}

const EmailEntryPage = ({ coreAccount = null }: EmailEntryPageProps) => {
  const router = useRouter()
  const { t } = useTranslation()
  const fireEvent = useFireEvent()
  const basePath = getBasePath()

  const { country } = useGeo()
  const isFacebookEnabled = useIsSocialLoginsFacebookEnabled()
  const isGoogleEnabled = useIsSocialLoginsGoogleEnabled()
  const { data: cohortDetails } = useCohortDetails()
  const isCohortClosed = cohortDetails?.state === 'closed'

  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [signedInAccount, setSignedInAccount] = useState(coreAccount)

  // Read error from query params (set by callback.api.ts on OAuth failures)
  useEffect(() => {
    const queryError = router.query.error
    if (typeof queryError === 'string' && ERROR_MESSAGES[queryError]) {
      setError(t(ERROR_MESSAGES[queryError]))
      // Clear error from URL to avoid showing stale errors on refresh
      const { error: _error, ...restQuery } = router.query
      router.replace(
        { pathname: router.pathname, query: restQuery },
        undefined,
        {
          shallow: true,
        },
      )
    }
  }, [router.query.error]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    // When user arrived via auth redirect (returnTo present), go back to
    // the originating page (e.g. performance page or enrollment page).
    if (router.query.returnTo) {
      router.back()
    } else {
      router.push(paths.programDetails)
    }
  }

  const handleAppleLogin = useCallback(async () => {
    setIsLoading(true)
    setError('')

    try {
      const appleData = await initAndSignInWithApple()

      const response = await fetch(`${basePath}/api/auth/apple-callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          authorization: appleData.authorization,
          user: appleData.user,
          fbCookies: getFbCookies(),
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        if (data.error === 'apple_signup_blocked') {
          setError(t('emailEntry.appleSignupBlocked'))
        } else {
          setError(t('emailEntry.genericError'))
        }
        setIsLoading(false)
        return
      }

      window.location.href = data.redirectTo
    } catch (err) {
      // Apple popup errors include: user closed popup, SDK not configured, SDK failed to load
      // Only suppress the "popup closed" case (Apple throws a specific error with type "popup_closed_by_user" for that)
      const isPopupClosed =
        err instanceof Error && err.message?.includes('popup_closed')
      if (!isPopupClosed) {
        // eslint-disable-next-line no-console
        console.error('Apple sign-in error:', err)
        trackError(err instanceof Error ? err : String(err), {
          page: educatorPageNames.EMAIL_ENTRY,
          step: 'apple-login',
        })
        setError(t('emailEntry.genericError'))
      }
      setIsLoading(false)
    }
  }, [basePath, t])

  const handleContinueAsSignedIn = async () => {
    if (!signedInAccount) {
      return
    }
    setIsLoading(true)
    setError('')

    try {
      const response = await fetch(`${basePath}/api/auth/core-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expectedEmail: signedInAccount.email,
          returnTo: getSafeReturnTo(router.query.returnTo),
        }),
      })
      if (response.status === 409) {
        const { account } = (await response.json()) as {
          account?: CoreSessionAccount
        }
        setSignedInAccount(account?.email ? { email: account.email } : null)
        setIsLoading(false)
        return
      }
      if (!response.ok) {
        throw new Error(`core-session sign-in failed (${response.status})`)
      }

      const data = await response.json()
      window.location.href = data.redirectTo
    } catch (err) {
      trackError(err instanceof Error ? err : String(err), {
        page: educatorPageNames.EMAIL_ENTRY,
        step: 'core-session-login',
      })
      setSignedInAccount(null)
      setError(t('emailEntry.genericError'))
      setIsLoading(false)
    }
  }

  const handleNotYou = async () => {
    setIsLoading(true)
    setError('')

    if (await signOutOfCore()) {
      setSignedInAccount(null)
    } else {
      setError(t('emailEntry.genericError'))
    }
    setIsLoading(false)
  }

  const handleSocialLogin = async (provider: SocialProvider) => {
    if (provider === 'apple') {
      handleAppleLogin()
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const returnTo = `${basePath}/auth/post-login`
      const response = await fetch(`${basePath}/api/auth/social`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ provider, returnTo, fbCookies: getFbCookies() }),
      })

      const data = await response.json()

      if (!response.ok) {
        setError(data.error || t('emailEntry.genericError'))
        setIsLoading(false)
      } else {
        window.location.href = data.authorizationUrl
      }
    } catch (err) {
      trackError(err instanceof Error ? err : String(err), {
        page: educatorPageNames.EMAIL_ENTRY,
        step: 'social-login',
        provider,
      })
      setError(t('emailEntry.genericError'))
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate email
    const result = emailSchema.safeParse(email)
    if (!result.success) {
      setError(t('emailEntry.invalidEmail'))
      return
    }

    setIsLoading(true)

    try {
      const response = await fetch(`${basePath}/api/auth/check-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (!response.ok) {
        setError(data.error || t('emailEntry.genericError'))
        setIsLoading(false)
        return
      }

      const isGdpr = country === 'XX' || isGDPRCountry(country ?? '')
      if (!isGdpr) {
        fireEvent('educatorPreCartEmailCaptured', {
          page: educatorPageNames.EMAIL_ENTRY,
          email,
          location: 'email form',
          email_capture_method: 'user_added',
          marketing_consent: true,
          gdpr_opt_in: true,
        })
      }

      // Preserve a returnTo destination (e.g. a protected page the user was
      // bounced from) across the email → login hop so they land back there
      // after authenticating. getSafeReturnTo keeps the same-origin check in one
      // place (shared with LoginPage) so the two copies can't drift.
      //
      // NB: returnTo is intentionally only forwarded on the existing-user →
      // login branch. New-user flows (waitlist and register below) funnel to
      // enrollment regardless: a brand-new user isn't entitled to the protected
      // returnTo target yet, and the target page's own getServerSideProps guards
      // would re-route them into enrollment anyway, so carrying returnTo would
      // only add a redirect hop to the same place.
      const returnTo = getSafeReturnTo(router.query.returnTo)

      // Route based on whether email exists
      // Email is now stored in HTTP-only cookie by check-email API
      if (data.exists) {
        router.push(
          returnTo
            ? { pathname: paths.login, query: { returnTo } }
            : paths.login,
        )
      } else if (isCohortClosed && !data.isReferred) {
        // New user (no account) after the enrollment deadline: send straight to
        // the waitlist instead of registering — don't create an account.
        // Fast Track Referral nominees are exempt and register as usual.
        router.push(paths.enrollmentWaitlist)
      } else {
        router.push(paths.register)
      }
    } catch (err) {
      trackError(err instanceof Error ? err : String(err), {
        page: educatorPageNames.EMAIL_ENTRY,
        step: 'email-submit',
      })
      setError(t('emailEntry.genericError'))
      setIsLoading(false)
    }
  }

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.currentTarget.form?.requestSubmit()
    }
  }

  return (
    <TrackingAttributes page={educatorPageNames.EMAIL_ENTRY}>
      <div className='tailwind-enabled min-h-svh bg-ja-mint-300 flex flex-col'>
        <ThemeColor color='mint' />
        <TrackPageViewed
          name={educatorPageNames.EMAIL_ENTRY}
          options={{ integrations: GOLD_STANDARD_INTEGRATIONS }}
        />
        <TrackOnMount
          event='educatorSignViewed'
          name={educatorPageNames.EMAIL_ENTRY}
        />

        <Header onClose={handleClose} />

        {/* Main content - flex-1 centers form vertically */}
        <div className='flex-1 flex items-center justify-center px-5 md:px-0'>
          <div className='w-full max-w-[329px] md:max-w-[400px] flex flex-col gap-8'>
            {/* Title section */}
            <div className='flex flex-col gap-5 w-full md:text-center'>
              <h1 className='font-serif font-medium text-[32px] leading-[1.15] text-text-primary m-0'>
                {t('emailEntry.title')}
              </h1>
              <p className='font-sans text-base leading-[1.35] tracking-[-0.16px] text-text-secondary-transparent m-0'>
                {t('emailEntry.subtitle')}
              </p>
            </div>

            {signedInAccount && (
              <CoreSessionContinue
                email={signedInAccount.email}
                onContinue={handleContinueAsSignedIn}
                onNotYou={handleNotYou}
                disabled={isLoading}
              />
            )}

            {/* Social login buttons */}
            <SocialLoginButtons
              onSocialLogin={handleSocialLogin}
              disabled={isLoading}
              isGoogleEnabled={isGoogleEnabled}
              isFacebookEnabled={isFacebookEnabled}
            />

            {/* Email form */}
            <TrackingAttributes location='email form'>
              <div className='flex flex-col gap-6 w-full'>
                <form
                  onSubmit={handleSubmit}
                  className='flex flex-col gap-6 w-full'
                >
                  <div className='flex flex-col gap-2 w-full'>
                    <label
                      htmlFor='email'
                      className='font-sans text-xs leading-none text-text-secondary'
                    >
                      {t('emailEntry.emailLabel')}
                    </label>
                    <input
                      id='email'
                      type='email'
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={handleEmailKeyDown}
                      className='w-full h-11 px-3 pb-[2px] bg-surface-input border-none font-sans text-sm max-md:text-[16px] leading-none text-text-primary placeholder:text-text-disabled focus:outline-none focus:ring-1 focus:ring-border-black disabled:opacity-50 disabled:cursor-not-allowed'
                      disabled={isLoading}
                      autoComplete='email'
                    />
                  </div>

                  {error && (
                    <div
                      className='py-2 px-3 bg-surface-error-bg text-text-error-1 font-sans text-xs leading-relaxed text-center -mt-3'
                      role='alert'
                    >
                      {error}
                    </div>
                  )}

                  <div className='flex flex-col gap-3 items-center w-full'>
                    <TrackClick
                      event='educatorClick'
                      action='continue with email'
                    >
                      <button
                        type='submit'
                        className='w-full h-12 px-4 bg-button-primary-default border-none font-sans font-semibold text-sm leading-none tracking-[0.14px] text-text-invert cursor-pointer transition-colors flex items-center justify-center hover:enabled:bg-button-primary-hover disabled:opacity-50 disabled:cursor-not-allowed'
                        disabled={isLoading}
                      >
                        {t('emailEntry.continueButton')}
                      </button>
                    </TrackClick>

                    <TermsText continueAsEmail={signedInAccount?.email} />
                  </div>
                </form>
              </div>
            </TrackingAttributes>
          </div>
        </div>

        {/* Decorative blocks at bottom - same max-width as form */}
        <div className='flex justify-center px-5 md:px-0 [@media(max-height:700px)]:hidden'>
          <div className='w-full max-w-[329px] md:max-w-[400px]'>
            <DecorativeBlocks className='w-full h-auto' />
          </div>
        </div>
      </div>
    </TrackingAttributes>
  )
}

export { EmailEntryPage }
